"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { z } from "zod";

import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormEmail,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormPassword,
  FormSubmitButton,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Stack } from "@/components/ui/stack";

import { fetchApi } from "@/lib/api/client";
import { zRequiredEmail } from "@/lib/form-schema";

const schema = z.object({
  email: zRequiredEmail,
  name: z.string().trim().min(1, "Required"),
  password: z
    .string()
    .min(10, "Password must be at least 10 characters")
    .regex(/\d/, "Password must include at least 1 number")
    .regex(/[a-zA-Z]/, "Password must include at least 1 letter"),
});

// NOTE: User will be redirected if they are already logged in. See middleware.ts.
function SignupForm({
  searchParams,
}: {
  searchParams?: ReturnType<typeof useSearchParams>;
}) {
  const [googleError, setGoogleError] = useState<string | null>(null);

  const onSubmit = async (values: z.output<typeof schema>) => {
    const response = await fetchApi("users/create_from_email_password", {
      method: "POST",
      body: JSON.stringify({
        email: values.email,
        name: values.name,
        password: values.password,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Failed to create account");
    }

    const returnTo = searchParams?.get("returnTo");
    window.location.href = returnTo || "/verify-email";
  };

  return (
    <Stack gap={6} className="w-full">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Create your account</CardTitle>
        </CardHeader>
        <CardContent>
          <Form
            schema={schema}
            defaultValues={{
              email: searchParams?.get("email") ?? "",
              name: "",
              password: "",
            }}
            onSubmit={onSubmit}
          >
            <Stack gap={4}>
              <FormField name="email">
                <FormItem id="email">
                  <FormLabel>Email</FormLabel>
                  <FormEmail
                    required
                    defaultValue={searchParams?.get("email") ?? ""}
                  />
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField name="name">
                <FormItem id="name">
                  <FormLabel>Full name</FormLabel>
                  <FormControl>
                    <Input type="text" autoComplete="name" required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField name="password">
                <FormItem id="password">
                  <FormLabel>Password</FormLabel>
                  <FormPassword required autoComplete="new-password" />
                  <FormDescription>
                    At least 10 characters, with a letter and a number.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormSubmitButton
                className="w-full"
                pending="Creating account..."
              >
                Create account
              </FormSubmitButton>
            </Stack>
          </Form>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                OR
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <GoogleSignInButton handleError={setGoogleError} />
            {googleError && (
              <p className="text-sm text-destructive">{googleError}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="p-0">
          <div className="w-full rounded-b-xl bg-muted/50 px-6 py-4 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              href="/login"
              className="underline underline-offset-4 hover:text-primary"
            >
              Sign in
            </Link>
          </div>
        </CardFooter>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground">
        By creating an account, you agree to our <br />
        <Link
          href="/terms"
          className="underline underline-offset-4 hover:text-primary"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href="/privacy"
          className="underline underline-offset-4 hover:text-primary"
        >
          Privacy Policy
        </Link>
        .
      </div>
    </Stack>
  );
}

// Reading search parameters through useSearchParams() without a Suspense boundary will
// opt the entire page into client-side rendering. This could cause your page to be blank
// until the client-side JavaScript has loaded.
// https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
function SignupFormWithSearchParams() {
  const searchParams = useSearchParams();
  return <SignupForm searchParams={searchParams} />;
}

export default function SignupPage() {
  return (
    <Suspense fallback={<SignupForm />}>
      <SignupFormWithSearchParams />
    </Suspense>
  );
}
